import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { TriggerType } from 'dashboard/models/trigger';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import Action from 'lib/interfaces/Action';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

interface WorkflowRunsPayload {
  workflowRuns: WorkflowRun[];
  environment: Environment;
  paginationParams?: PaginationParams;
}

export interface WorkflowRunPayload {
  workflowRun: Workflow;
}

interface WorkflowsPayload {
  workflows: Record<string, Workflow>;
}
interface WorkflowVersionsPayload {
  workflowVersions: Record<string, WorkflowVersion>;
}

export interface WorkflowRunsFilterParams {
  completedAtStart?: Iso8601DateString;
  completedAtEnd?: Iso8601DateString;
  status: WorkflowRunStatus[];
  workflowIds: string[];
  workflowVersionIds: string[];
  omni?: string;
  triggerType?: TriggerType;
  triggerObjectId?: string;
  simulate?: boolean;
}

interface WorkflowRunFiltersPayload {
  filters: WorkflowRunsFilterParams;
}

interface WorkflowRunSortParamsPayload {
  sortParams: SortParams;
}

interface CurrentPagePayload {
  currentPage: number;
}

interface PageSizePayload {
  pageSize: number;
}

interface ManualRunResultsPayload {
  validObjectIds: string[];
  invalidObjectIds: string[];
}

export type WorkflowRunsActions =
  | Action<WorkflowRunsActionType.SetWorkflowRuns, WorkflowRunsPayload>
  | Action<WorkflowRunsActionType.SetWorkflows, WorkflowsPayload>
  | Action<WorkflowRunsActionType.SetWorkflowVersions, WorkflowVersionsPayload>
  | Action<WorkflowRunsActionType.SetWorkflowRunSortParams, WorkflowRunSortParamsPayload>
  | Action<WorkflowRunsActionType.SetWorkflowRunFilters, WorkflowRunFiltersPayload>
  | Action<WorkflowRunsActionType.SetCurrentPage, CurrentPagePayload>
  | Action<WorkflowRunsActionType.SetPageSize, PageSizePayload>
  | Action<WorkflowRunsActionType.SetManualRunResults, ManualRunResultsPayload>
  | Action<WorkflowRunsActionType.ClearManualRunResults>;

export enum WorkflowRunsActionType {
  SetWorkflowRuns = 'workflow-runs/SET_WORKFLOW_RUNS',
  SetWorkflows = 'workflow-runs/SET_WORKFLOWS',
  SetWorkflowVersions = 'workflow-runs/SET_WORKFLOW_VERSIONS',
  SetWorkflowRunFilters = 'workflow-runs/SET_WORKFLOW_RUN_FILTERS',
  SetWorkflowRunSortParams = 'workflow-runs/SET_WORKFLOW_RUN_SORT_PARAMS',
  SetCurrentPage = 'workflow-runs/SET_CURRENT_PAGE',
  SetPageSize = 'workflow-runs/SET_PAGE_SIZE',
  SetManualRunResults = 'workflow-runs/SET_MANUAL_RUN_RESULTS',
  ClearManualRunResults = 'workflow-runs/CLEAR_MANUAL_RUN_RESULTS',
}

export enum WorkflowRunActionType {
  SetWorkflowRun = 'workflow_run/SET_WORKFLOW_RUN',
}

export function setWorkflowRuns(
  workflowRuns: WorkflowRun[],
  environment: Environment,
  paginationParams: PaginationParams,
) {
  return {
    payload: {
      workflowRuns,
      environment,
      paginationParams,
    },
    type: WorkflowRunsActionType.SetWorkflowRuns,
  };
}

export function setWorkflows(workflows: Record<string, Workflow>) {
  return {
    payload: {
      workflows,
    },
    type: WorkflowRunsActionType.SetWorkflows,
  };
}

export function setWorkflowVersions(workflowVersions: Record<string, WorkflowVersion>) {
  return {
    payload: {
      workflowVersions,
    },
    type: WorkflowRunsActionType.SetWorkflowVersions,
  };
}

export function setWorkflowRunFilters(filters: Partial<WorkflowRunsFilterParams>) {
  return {
    payload: {
      filters,
    },
    type: WorkflowRunsActionType.SetWorkflowRunFilters,
  };
}

export function setWorkflowRunSortParams(sortParams: SortParams) {
  return {
    payload: {
      sortParams,
    },
    type: WorkflowRunsActionType.SetWorkflowRunSortParams,
  };
}

export function setCurrentPage(currentPage: number) {
  return {
    payload: {
      currentPage,
    },
    type: WorkflowRunsActionType.SetCurrentPage,
  };
}

export function setPageSize(pageSize: number) {
  return {
    payload: {
      pageSize,
    },
    type: WorkflowRunsActionType.SetPageSize,
  };
}

export function setManualRunResults(validObjectIds: string[], invalidObjectIds: string[]) {
  return {
    payload: {
      validObjectIds,
      invalidObjectIds,
    },
    type: WorkflowRunsActionType.SetManualRunResults,
  };
}

export function clearManualRunResults() {
  return {
    type: WorkflowRunsActionType.ClearManualRunResults,
  };
}

export function setWorkflowRun(
  workflowRun: WorkflowRunPayload['workflowRun'],
): Action<WorkflowRunActionType.SetWorkflowRun, WorkflowRunPayload> {
  return {
    payload: {
      workflowRun,
    },
    type: WorkflowRunActionType.SetWorkflowRun,
  };
}
